import React from 'react';
import { cookies } from 'store/apis';

type Body<T> = {
  readonly body: ReadableStream<Uint8Array> | null;
  readonly bodyUsed: boolean;
  arrayBuffer(): Promise<ArrayBuffer>;
  blob(): Promise<Blob>;
  formData(): Promise<FormData>;
  json(): Promise<{ data: T; error: Error }>;
  text(): Promise<string>;
};

type CustomResponse<T> = {
  readonly headers: Headers;
  readonly ok: boolean;
  readonly redirected: boolean;
  readonly status: number;
  readonly statusText: string;
  readonly type: ResponseType;
  readonly url: string;
  clone(): Response;
} & Body<T>;

type UseFetcherProps<T> = {
  data: T | null | undefined;
  isLoading: boolean;
  isError: Error | null | undefined;
  mutate: () => void;
};

/**
 *
 * @param resource - endpoint from after e.g. (/api/v2/some-resource/method)
 * @param options - RequestInit the config object. defaults:
 * ```js
 * {
    method: 'GET',
    //don't move position keeps headers from being overwritten
    //in the event that additional headers are given.
    ...options,
    headers: {
      authorization: process.env.REACT_APP_AUTHORIZATION_HEADER,
      'Content-Type': 'application/json',
      'authorization-token': cookies.load('authToken'),
      ...options?.headers,
    }
 * ```
    @type the "T" generic type should be the data from the response you expect.
 * @returns
 */
export const fetcher = <T = unknown>(resource: RequestInfo | URL, options?: RequestInit) =>
  fetch(`${process.env.REACT_APP_BASE_API_URL || ''}${resource}`, {
    method: 'GET',
    //don't move position keeps headers from being overwritten
    //in the event that additional headers are given.
    ...options,
    headers: {
      authorization: process.env.REACT_APP_AUTHORIZATION_HEADER,
      'Content-Type': 'application/json',
      'authorization-token': cookies.load('authToken'),
      ...options?.headers,
    } as HeadersInit,
  }).then((res: CustomResponse<T>) => res.json());

/**
 *
 * @param url - endpoint from which you want data.
 * @returns
 */
export function useFetcher<T>(url: string): UseFetcherProps<T> {
  const [mount, setMount] = React.useState(false);
  const [data, setData] = React.useState<T>();
  const [error, setError] = React.useState<Error>();
  const [mutate, setMutate] = React.useState(false);

  React.useEffect(() => {
    async function fetchData() {
      try {
        const { data, error } = await fetcher<T>(url);
        if (error) setError(error);
        if (data && !error) setData(data);
      } catch (error) {
        if (error instanceof Error) setError(error);
      }
    }

    if (mount && !data && !error) fetchData();
    if (mutate) {
      fetchData();
      setMutate(false);
    }

    setMount(true);
    return () => setMount(false);
  }, [data, error, mount, mutate, url]);

  return {
    isLoading: !data && !error,
    isError: error,
    data: data,
    mutate: () => setMutate(true),
  };
}

export default fetcher;
